import React from 'react';
import { useTranslation } from 'react-i18next';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { type MarkdownStorage } from 'tiptap-markdown';

import { Modal } from '../../../../components/Modal';
import { Field } from '../../../../components/Field';
import { Button } from '../../../../components/Button';

import { CALLOUT_KINDS, type CalloutKind } from './callout';
import { type DialectBlockKind } from './dialectBlock';
import { bodyEditorExtensions } from './extensions';
import {
  Root,
  Frame,
  Toolbar,
  ToolGroup,
  ToolButton,
  LumioToolButton,
  LumioAction,
  LumioMark,
  LinkRow,
  LinkInput,
  LinkAction,
  SourceArea,
  SourceHint,
  Content,
  ResizeHandle,
  ErrorRow,
} from './MarkdownEditor.styles';
// The quiz block's rim animates the same registered angle every other wash on
// the page does, so the registration comes from the same place.
import { GlowAngleProperty } from '../GeneratingWash/GeneratingWash.styles';

/** A quiz as the writer hands it over: the question, and the dialect body under it. */
export type QuizDraft = {
  /** The question, which is what follows `::: quiz` on the fence line. */
  question: string;
  /** The options (`- [ ]` / `- [x]`) and the explanation, verbatim. */
  source: string;
};

export type MarkdownEditorProps = {
  /**
   * Names the toolbar for assistive tech. NOT rendered: the visible heading is
   * the panel's own `SectionTitle`, so Content is titled the way Article details
   * and SEO are rather than with a field label of its own.
   */
  label: string;
  /** Markdown at mount. Uncontrolled after that — key the component to reset. */
  initialMarkdown: string;
  /** Fired with the serialized markdown on every document change. */
  onChange: (markdown: string) => void;
  /**
   * The content area's height at mount, in pixels. Uncontrolled after that: the
   * handle owns it. Reported back through `onHeightChange` so the page can hold
   * it across a remount (this component is keyed by article id, and the first
   * save of a new article changes that key).
   */
  initialHeight?: number;
  /** Fired when a resize settles, not while the handle is moving. */
  onHeightChange?: (height: number) => void;
  /**
   * Asks Lumio to write the quiz. Rejects with the message to show (the blog
   * service returns refusals as UI copy). Leave it out when generation is off
   * for the environment: the editor then offers no Lumio control at all, rather
   * than one that fails when pressed.
   */
  onWriteQuiz?: (current: QuizDraft) => Promise<QuizDraft>;
  /** Credit cost of one quiz write; carried on the Lumio quiz controls once priced. */
  quizCost?: number;
  /**
   * Fired when a quiz starts and finishes being written. The wait belongs to the
   * whole page, not just to this editor: the rule is that nothing is pressable
   * while anything is being written, and Save is on the page.
   */
  onBusyChange?: (busy: boolean) => void;
  /**
   * Something is being written on the page. Every toolbar control goes inert AND
   * the document stops accepting keystrokes: a body being rewritten under the
   * caret would otherwise take edits that the arriving text overwrites.
   */
  disabled?: boolean;
};

const CALLOUT_ICON: Record<CalloutKind, string> = {
  tip: 'bi-lightbulb',
  warning: 'bi-exclamation-triangle',
  note: 'bi-info-circle',
  key: 'bi-key',
};

const LUMIO_MARK_SRC = '/assets/lumio/lumio-mark.svg';

/** Content-area height: the bounds the handle may drag between, and its keyboard step. */
const HEIGHT = { MIN: 220, MAX: 1400, DEFAULT: 420, STEP: 40 } as const;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const getMarkdown = (editor: Editor): string => (editor.storage.markdown as MarkdownStorage).getMarkdown();

/**
 * WYSIWYG body editor for the whitelabels markdown dialect
 * (`docs/blog-authoring-plan.md`): H2/H3, bold/italic/code, lists, blockquote,
 * fenced code, tables, images (figures), links, the four `:::` callouts and the
 * three engagement blocks (toggle, quiz, infographic). TipTap edits rich text;
 * `tiptap-markdown` parses/serializes, so the form only ever sees markdown.
 * Mock stage: body images embed as data URLs; the media API later swaps in CDN
 * URLs at upload time with no editor change.
 *
 * Everything the article GENERATOR can produce is reachable from the toolbar, so
 * an owner who liked a generated article's quiz can add a second one by hand
 * rather than regenerating the whole piece. The quiz is the one block nobody is
 * expected to write themselves, and its two controls (insert, and rewrite the
 * selected one) carry Lumio's own mark and border.
 */
export const MarkdownEditor = ({
  label,
  initialMarkdown,
  onChange,
  initialHeight = HEIGHT.DEFAULT,
  onHeightChange,
  onWriteQuiz,
  quizCost,
  onBusyChange,
  disabled = false,
}: MarkdownEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  // Insert-image dialog: the picked file waits here while the author writes the
  // alt text + caption (the caption is the markdown `title`, rendered as the
  // figcaption by the dialect).
  const [pendingImage, setPendingImage] = React.useState<{ src: string; alt: string; caption: string } | null>(null);
  const [imageOpen, setImageOpen] = React.useState(false);
  const imageInputRef = React.useRef<HTMLInputElement>(null);

  const [height, setHeightState] = React.useState(() => clamp(initialHeight, HEIGHT.MIN, HEIGHT.MAX));
  /** The height as the drag has it right now, readable when the pointer lifts. */
  const heightRef = React.useRef(height);
  const resizeRef = React.useRef<{ startY: number; startHeight: number } | null>(null);

  /** Source dialog for the selected engagement block. */
  const [blockEdit, setBlockEdit] = React.useState<{ title: string; source: string } | null>(null);
  const [quizBusy, setQuizBusy] = React.useState(false);
  const [quizError, setQuizError] = React.useState<string | null>(null);
  /**
   * Where the quiz being written lives, tracked THROUGH the edits the author
   * makes while they wait. The call takes long enough to keep typing during, and
   * a position captured when it started would by then point at the wrong node —
   * or into the middle of one.
   */
  const quizPos = React.useRef<number | null>(null);

  const editor = useEditor({
    // The list lives in ./extensions so the round-trip component test can run
    // exactly what the editor runs; a test that rebuilt the list would pass
    // while this one fell behind the dialect. The placeholder is evaluated once
    // at creation, so a mid-session language switch keeps the old copy until
    // the editor remounts (known limitation).
    extensions: bodyEditorExtensions(t('Start writing your article')),
    content: initialMarkdown,
    onUpdate: ({ editor: current }) => onChange(getMarkdown(current)),
    onTransaction: ({ transaction }) => {
      if (quizPos.current !== null) quizPos.current = transaction.mapping.map(quizPos.current);
    },
  });

  React.useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const setHeight = (next: number): void => {
    const clamped = clamp(next, HEIGHT.MIN, HEIGHT.MAX);
    heightRef.current = clamped;
    setHeightState(clamped);
  };

  const onHandlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeRef.current = { startY: event.clientY, startHeight: heightRef.current };
  };

  const onHandlePointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const start = resizeRef.current;
    if (!start) return;
    setHeight(start.startHeight + (event.clientY - start.startY));
  };

  const onHandlePointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!resizeRef.current) return;
    resizeRef.current = null;
    onHeightChange?.(heightRef.current);
  };

  const onHandleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    setHeight(heightRef.current + (event.key === 'ArrowDown' ? HEIGHT.STEP : -HEIGHT.STEP));
    onHeightChange?.(heightRef.current);
  };

  if (!editor) return <Root />;

  const applyLink = (): void => {
    const url = linkUrl.trim();
    if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkOpen(false);
    setLinkUrl('');
  };

  const toggleLink = (): void => {
    if (editor.isActive('link')) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    setLinkOpen((open) => !open);
  };

  const pickImage = async (file: File): Promise<void> => {
    const src = await readFileAsDataUrl(file);
    // Alt prefilled from the file name; both fields stay editable in the dialog.
    const alt = file.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ');
    setPendingImage({ src, alt, caption: '' });
    setImageOpen(true);
  };

  const confirmInsertImage = (): void => {
    if (!pendingImage) return;
    setImageOpen(false);
    editor
      .chain()
      .focus()
      .setImage({ src: pendingImage.src, alt: pendingImage.alt, title: pendingImage.caption || undefined })
      .run();
  };

  const inTable = editor.isActive('table');

  /* ── The engagement blocks ── */

  const blockSelected = editor.isActive('dialectBlock');
  const blockAttrs = editor.getAttributes('dialectBlock');
  const blockKind = blockSelected ? String(blockAttrs.kind ?? '') : '';
  /** Position of the selected block, or null when the selection is not on one. */
  const selectedBlockPos = (): number | null => {
    const { from } = editor.state.selection;
    return editor.state.doc.nodeAt(from)?.type.name === 'dialectBlock' ? from : null;
  };

  /**
   * Rewrite one block in place. `transient` keeps the change out of the undo
   * stack, which is what the generating flag wants: it is a state of the UI, not
   * an edit the author made, and Ctrl+Z should never bring it back.
   */
  const updateBlockAt = (pos: number, attrs: Record<string, unknown>, transient = false): boolean => {
    const node = editor.state.doc.nodeAt(pos);
    if (!node || node.type.name !== 'dialectBlock') return false;
    const tr = editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs });
    if (transient) tr.setMeta('addToHistory', false);
    editor.view.dispatch(tr);
    return true;
  };

  const STARTER: Record<DialectBlockKind, { title: string; source: string }> = {
    toggle: { title: t('A question readers ask'), source: t('The answer, in a sentence or two.') },
    quiz: {
      title: t('Your question'),
      source: [
        `- [ ] ${t('An answer that is wrong')}`,
        `- [x] ${t('The answer that is right')}`,
        t('Why that is the right answer.'),
      ].join('\n'),
    },
    infographic: { title: t('What the diagram shows'), source: '' },
  };

  const insertBlock = (kind: DialectBlockKind): void => {
    editor.chain().focus().insertDialectBlock({ kind, ...STARTER[kind] }).run();
  };

  /**
   * Hand the selected quiz to Lumio. The block keeps its place and its old
   * content while the service writes: the card glows at its rim and the source
   * recedes behind drifting colour, so the wait happens where the result will
   * land rather than in a spinner somewhere else.
   */
  const writeQuiz = async (options?: { fresh?: boolean }): Promise<void> => {
    if (!onWriteQuiz || quizBusy) return;
    const pos = selectedBlockPos();
    if (pos === null) return;
    const node = editor.state.doc.nodeAt(pos);
    if (!node) return;

    setQuizError(null);
    setQuizBusy(true);
    onBusyChange?.(true);
    quizPos.current = pos;
    updateBlockAt(pos, { generating: true }, true);

    try {
      const next = await onWriteQuiz({
        // A block that has just been inserted asks the TEMPLATE's question, and
        // sending that back would have the writer told to avoid "Your question".
        // Only a real regenerate names the question to steer away from.
        question: options?.fresh ? '' : String(node.attrs.title),
        source: options?.fresh ? '' : String(node.attrs.source),
      });
      const at = quizPos.current;
      if (at !== null) updateBlockAt(at, { title: next.question, source: next.source, generating: false });
    } catch (err) {
      const at = quizPos.current;
      if (at !== null) updateBlockAt(at, { generating: false }, true);
      setQuizError(err instanceof Error ? err.message : String(err));
    } finally {
      quizPos.current = null;
      setQuizBusy(false);
      onBusyChange?.(false);
    }
  };

  const insertQuiz = (): void => {
    // The block goes in COMPLETE, template and all, even on Lumio's path. It is
    // in the document from this moment on, and the author may hit Save (or walk
    // away) during the seconds the writer takes: a block left empty until the
    // answer arrives would be stored as a quiz with no options, which the public
    // renderer emits as a broken one. `fresh` is what keeps the template out of
    // the prompt.
    insertBlock('quiz');
    if (onWriteQuiz) void writeQuiz({ fresh: true });
  };

  const openBlockEditor = (): void =>
    setBlockEdit({ title: String(blockAttrs.title ?? ''), source: String(blockAttrs.source ?? '') });

  const applyBlockEdit = (): void => {
    const pos = selectedBlockPos();
    if (pos !== null && blockEdit) updateBlockAt(pos, { title: blockEdit.title, source: blockEdit.source });
    setBlockEdit(null);
  };

  // Localized callout names for the toolbar labels (same copy the preview
  // renders as the callout label).
  const calloutNames: Record<CalloutKind, string> = {
    tip: t('Tip'),
    warning: t('Heads up'),
    note: t('Note'),
    key: t('Key point'),
  };

  /**
   * One toolbar button. The name is both the accessible name and the `title`, so
   * a pointer user reads the same thing a screen reader hears — a toolbar of
   * unlabelled glyphs is otherwise a guessing game.
   */
  const tool = (
    name: string,
    icon: string,
    onClick: () => void,
    state?: { active?: boolean; pressed?: boolean; disabled?: boolean },
  ): React.ReactElement => (
    <ToolButton
      key={name}
      type="button"
      aria-label={name}
      title={name}
      aria-pressed={state?.pressed}
      $active={state?.active ?? state?.pressed}
      disabled={disabled || state?.disabled}
      onClick={onClick}
    >
      <i className={`bi ${icon}`} aria-hidden="true" />
    </ToolButton>
  );

  return (
    <Root>
      <GlowAngleProperty />
      <Frame>
        <Toolbar role="toolbar" aria-label={label}>
          <ToolGroup>
            {tool(t('Undo'), 'bi-arrow-counterclockwise', () => editor.chain().focus().undo().run(), {
              disabled: !editor.can().undo(),
            })}
            {tool(t('Redo'), 'bi-arrow-clockwise', () => editor.chain().focus().redo().run(), {
              disabled: !editor.can().redo(),
            })}
          </ToolGroup>

          <ToolGroup>
            {tool(t('Section heading'), 'bi-type-h2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), {
              pressed: editor.isActive('heading', { level: 2 }),
            })}
            {tool(t('Subheading'), 'bi-type-h3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), {
              pressed: editor.isActive('heading', { level: 3 }),
            })}
          </ToolGroup>

          <ToolGroup>
            {tool(t('Bold'), 'bi-type-bold', () => editor.chain().focus().toggleBold().run(), {
              pressed: editor.isActive('bold'),
            })}
            {tool(t('Italic'), 'bi-type-italic', () => editor.chain().focus().toggleItalic().run(), {
              pressed: editor.isActive('italic'),
            })}
            {tool(t('Inline code'), 'bi-code', () => editor.chain().focus().toggleCode().run(), {
              pressed: editor.isActive('code'),
            })}
            {tool(t('Link'), 'bi-link-45deg', toggleLink, { pressed: editor.isActive('link') })}
          </ToolGroup>

          <ToolGroup>
            {tool(t('Bullet list'), 'bi-list-ul', () => editor.chain().focus().toggleBulletList().run(), {
              pressed: editor.isActive('bulletList'),
            })}
            {tool(t('Numbered list'), 'bi-list-ol', () => editor.chain().focus().toggleOrderedList().run(), {
              pressed: editor.isActive('orderedList'),
            })}
            {tool(t('Quote'), 'bi-quote', () => editor.chain().focus().toggleBlockquote().run(), {
              pressed: editor.isActive('blockquote'),
            })}
            {tool(t('Code block'), 'bi-code-square', () => editor.chain().focus().toggleCodeBlock().run(), {
              pressed: editor.isActive('codeBlock'),
            })}
            {tool(t('Divider line'), 'bi-hr', () => editor.chain().focus().setHorizontalRule().run())}
          </ToolGroup>

          <ToolGroup>
            {tool(t('Insert image'), 'bi-image', () => imageInputRef.current?.click())}
            {tool(
              t('Insert table'),
              'bi-table',
              () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
              { active: inTable },
            )}
          </ToolGroup>

          <ToolGroup>
            {CALLOUT_KINDS.map((kind) =>
              tool(
                t('Callout: {{kind}}', { kind: calloutNames[kind] }),
                CALLOUT_ICON[kind],
                () => editor.chain().focus().toggleCallout(kind).run(),
                { pressed: editor.isActive('callout', { kind }) },
              ),
            )}
          </ToolGroup>

          {/* The engagement blocks the article generator writes. The quiz is the
              one nobody hand-writes, so its button is Lumio's. */}
          <ToolGroup>
            {tool(t('Insert an expandable answer'), 'bi-chevron-bar-expand', () => insertBlock('toggle'))}
            {onWriteQuiz ? (
              // The quiz's own glyph, not Lumio's mark: this is the "insert a
              // quiz" button in a row of insert buttons, and it has to be
              // findable by what it inserts. The gradient ring is what carries
              // the other half of the meaning, that Lumio writes it.
              <LumioToolButton
                type="button"
                aria-label={
                  quizCost != null
                    ? quizCost === 1
                      ? t('Insert a quiz, written by Lumio · {{n}} credit', { n: quizCost })
                      : t('Insert a quiz, written by Lumio · {{n}} credits', { n: quizCost })
                    : t('Insert a quiz, written by Lumio')
                }
                title={
                  quizCost != null
                    ? quizCost === 1
                      ? t('Insert a quiz, written by Lumio · {{n}} credit', { n: quizCost })
                      : t('Insert a quiz, written by Lumio · {{n}} credits', { n: quizCost })
                    : t('Insert a quiz, written by Lumio')
                }
                disabled={disabled || quizBusy}
                onClick={insertQuiz}
              >
                <i className="bi bi-patch-question" aria-hidden="true" />
              </LumioToolButton>
            ) : (
              // Generation is off for this environment, or there is no body to
              // write about yet. The block is still insertable, it just comes as
              // an empty template with nobody offering to fill it in.
              tool(t('Insert a quiz'), 'bi-patch-question', () => insertBlock('quiz'))
            )}
            {tool(t('Insert an infographic'), 'bi-bar-chart', () => insertBlock('infographic'))}
          </ToolGroup>

          {inTable ? (
            <ToolGroup>
              {tool(t('Add row'), 'bi-plus-square', () => editor.chain().focus().addRowAfter().run())}
              {tool(t('Add column'), 'bi-layout-three-columns', () => editor.chain().focus().addColumnAfter().run())}
              {tool(t('Delete table'), 'bi-trash', () => editor.chain().focus().deleteTable().run())}
            </ToolGroup>
          ) : null}

          {/* What can be done to the selected block. It appears on selection like
              the table tools above, because an atom has no place of its own to
              hang a control off. */}
          {blockSelected ? (
            <ToolGroup>
              {blockKind === 'quiz' && onWriteQuiz ? (
                <LumioAction type="button" disabled={disabled || quizBusy} onClick={() => void writeQuiz()}>
                  <LumioMark src={LUMIO_MARK_SRC} alt="" aria-hidden="true" />
                  {quizBusy
                    ? t('Writing the quiz…')
                    : quizCost != null
                      ? quizCost === 1
                        ? t('Regenerate quiz · {{n}} credit', { n: quizCost })
                        : t('Regenerate quiz · {{n}} credits', { n: quizCost })
                      : t('Regenerate quiz')}
                </LumioAction>
              ) : null}
              {tool(t('Edit the block source'), 'bi-pencil', openBlockEditor, { disabled: quizBusy })}
              {tool(t('Delete the block'), 'bi-trash', () => editor.chain().focus().deleteSelection().run(), {
                disabled: quizBusy,
              })}
            </ToolGroup>
          ) : null}
        </Toolbar>

        {linkOpen ? (
          <LinkRow>
            <LinkInput
              value={linkUrl}
              placeholder={t('Paste a link (https://... or slug:article-slug)')}
              aria-label={t('Link')}
              onChange={(event) => setLinkUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyLink();
                }
              }}
            />
            <LinkAction type="button" onClick={applyLink}>
              {t('Apply')}
            </LinkAction>
            <LinkAction
              type="button"
              onClick={() => {
                setLinkOpen(false);
                setLinkUrl('');
              }}
            >
              {t('Cancel')}
            </LinkAction>
          </LinkRow>
        ) : null}

        {quizError ? <ErrorRow text={quizError} tag="span" /> : null}

        <Content $height={height}>
          <EditorContent editor={editor} />
        </Content>

        <ResizeHandle
          role="separator"
          aria-orientation="horizontal"
          aria-label={t('Resize the editor')}
          aria-valuenow={height}
          aria-valuemin={HEIGHT.MIN}
          aria-valuemax={HEIGHT.MAX}
          tabIndex={0}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          onKeyDown={onHandleKeyDown}
        />
      </Frame>

      <Modal
        open={imageOpen}
        title={t('Insert image')}
        onClose={() => setImageOpen(false)}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => setImageOpen(false)} />
            <Button label={t('Insert image')} onClick={confirmInsertImage} />
          </>
        }
      >
        {pendingImage ? (
          <>
            <Field
              label={t('Alt text')}
              value={pendingImage.alt}
              onChange={(alt) => setPendingImage((prev) => (prev ? { ...prev, alt } : prev))}
            />
            <Field
              label={t('Caption')}
              value={pendingImage.caption}
              onChange={(caption) => setPendingImage((prev) => (prev ? { ...prev, caption } : prev))}
            />
          </>
        ) : null}
      </Modal>

      <Modal
        open={blockEdit !== null}
        title={t('Edit the block source')}
        onClose={() => setBlockEdit(null)}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => setBlockEdit(null)} />
            <Button label={t('Save block')} onClick={applyBlockEdit} />
          </>
        }
      >
        {blockEdit ? (
          <>
            <Field
              label={blockKind === 'quiz' ? t('Question') : t('Title')}
              value={blockEdit.title}
              onChange={(title) => setBlockEdit((prev) => (prev ? { ...prev, title } : prev))}
            />
            <SourceHint
              text={
                blockKind === 'quiz'
                  ? t('One option per line as - [ ] or - [x], the right one marked with an x. Every other line is the explanation.')
                  : blockKind === 'infographic'
                    ? t('Inline SVG. Anything the public renderer will not accept is dropped from the page.')
                    : t('The body, in the same markdown the article uses.')
              }
              tag="span"
            />
            <SourceArea
              value={blockEdit.source}
              aria-label={t('Block source')}
              spellCheck={false}
              onChange={(event) => setBlockEdit((prev) => (prev ? { ...prev, source: event.target.value } : prev))}
            />
          </>
        ) : null}
      </Modal>

      {/* Hidden body-image picker (mock stage: data URLs; media API later). */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void pickImage(file);
          event.target.value = '';
        }}
      />
    </Root>
  );
};

export default MarkdownEditor;
